import { useEffect, useRef, useState } from "react"
import { X, ArrowLeft } from "lucide-react"
import {
  getCustomDestinationsService,
  removeCustomDestinationService,
} from "../../utils/customDestinationService"

// Custom Folders & Smart Email Routing (phase 1 plan 3). Mirrors the
// remove-specific error codes CustomFolderModal.js's ERROR_COPY_BY_CODE
// maps for save -- Remove is only ever offered on a custom row, so
// SYSTEM_MAPPING_INCOMPLETE/CUSTOM_DESTINATION_LIMIT_REACHED never apply
// here, but a concurrent edit or a model failure still can.
const REMOVE_ERROR_COPY_BY_CODE = {
  CUSTOM_DESTINATION_SNAPSHOT_CONFLICT: 'Folder settings changed. Refresh and try again.',
  CUSTOM_DESTINATION_RULE_GENERATION_FAILED: 'Could not update routing rules. Your folders have not changed.',
}
const DEFAULT_REMOVE_ERROR_COPY = 'Could not remove folder. Try again.'
const LOAD_DESTINATIONS_ERROR_COPY = 'Could not load folders. Try again.'
const SHADOWED_REASON_COPY = 'This folder was renamed in your mailbox to match a system folder and is temporarily unavailable. Remove it to free its slot.'

// Same shape FilteredEmailView already dispatches through setIntegrationDestinations
// (via CustomFolderModal's onSaved) -- keeps Redux's simplified {key,label}
// move-menu options in sync with whatever Preferences just changed.
const toSafeManagedDestinations = (managedDestinations) => (
  (managedDestinations ?? []).map(({ destinationKey, displayLabel }) => ({
    key: destinationKey,
    label: displayLabel,
  }))
)

const EmailCustomFolders = ({
  isOpen,
  onClose,
  onBack,
  mailIntegrationId,
  getToken,
  onOpenCustomFolderModal,
  onDestinationsChanged,
}) => {
  const [destinationsState, setDestinationsState] = useState('idle') // idle | loading | loaded | error
  const [managedDestinations, setManagedDestinations] = useState([])
  const [maxCustomDestinations, setMaxCustomDestinations] = useState(5)
  const [confirmingKey, setConfirmingKey] = useState(null)
  const [removingKey, setRemovingKey] = useState(null)
  const [removeErrorByKey, setRemoveErrorByKey] = useState({})
  const requestIdentityRef = useRef(null)

  // Fresh-fetch on every open (and on a mailbox switch while open): a
  // nonempty Redux-cached list is never treated as authoritative here.
  // Request-identity discard mirrors CustomFolderModal.js so a response
  // that resolves after Preferences closes or the mailbox switches is
  // never applied.
  useEffect(() => {
    if (!isOpen || typeof mailIntegrationId !== 'string' || mailIntegrationId.length === 0) {
      setDestinationsState('idle')
      return
    }

    const identity = { mailIntegrationId }
    requestIdentityRef.current = identity
    setDestinationsState('loading')
    setConfirmingKey(null)
    setRemoveErrorByKey({})

    getCustomDestinationsService(getToken, { mailIntegrationId })
      .then((result) => {
        if (requestIdentityRef.current !== identity) return
        setManagedDestinations(Array.isArray(result?.destinations) ? result.destinations : [])
        setMaxCustomDestinations(result?.maxCustomDestinations ?? 5)
        setDestinationsState('loaded')
      })
      .catch(() => {
        if (requestIdentityRef.current !== identity) return
        setDestinationsState('error')
      })

    return () => {
      if (requestIdentityRef.current === identity) requestIdentityRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mailIntegrationId])

  const handleRequestRemove = (destinationKey) => {
    setConfirmingKey(destinationKey)
    setRemoveErrorByKey((prev) => {
      const next = { ...prev }
      delete next[destinationKey]
      return next
    })
  }

  const handleCancelRemove = () => {
    setConfirmingKey(null)
  }

  const handleConfirmRemove = async (destinationKey) => {
    const identity = requestIdentityRef.current
    setRemovingKey(destinationKey)
    try {
      const result = await removeCustomDestinationService(getToken, { mailIntegrationId, destinationKey })
      if (requestIdentityRef.current !== identity) return
      const nextDestinations = Array.isArray(result?.destinations) ? result.destinations : []
      setManagedDestinations(nextDestinations)
      setMaxCustomDestinations(result?.maxCustomDestinations ?? maxCustomDestinations)
      setConfirmingKey(null)
      onDestinationsChanged?.({
        mailIntegrationId,
        destinations: toSafeManagedDestinations(nextDestinations),
      })
    } catch (error) {
      if (requestIdentityRef.current !== identity) return
      const code = error?.response?.data?.code
      setRemoveErrorByKey((prev) => ({
        ...prev,
        [destinationKey]: REMOVE_ERROR_COPY_BY_CODE[code] ?? DEFAULT_REMOVE_ERROR_COPY,
      }))
      // Failed removal leaves the active row visible -- confirmingKey stays
      // set so the error renders alongside the still-pending confirmation.
    } finally {
      if (requestIdentityRef.current === identity) setRemovingKey(null)
    }
  }

  const customDestinations = managedDestinations.filter((destination) => !destination.isSystem)
  const systemDestinations = managedDestinations.filter((destination) => destination.isSystem)
  const atCustomLimit = customDestinations.length >= maxCustomDestinations

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-[1000]">
      <div className="bg-white p-8 rounded-lg w-[90%] max-w-[600px] shadow-md">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-start gap-3">
            <button
              onClick={onBack}
              data-amp-track-section="Email Custom Folders"
              data-amp-track-action="Back_Click"
              data-amp-track-description="Back to Email Preferences"
              className="bg-transparent border-none hover:bg-gray-100 rounded-lg p-2 cursor-pointer text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex flex-col gap-2">
              <h2 className="m-0 text-gray-800">Custom Folders</h2>
              <p className="text-gray-600 text-sm">
                Geoffrey routes mail into your system folders automatically. Add a custom folder to route specific emails there too.
              </p>
            </div>
          </div>
          <button
            className="bg-transparent border-none hover:bg-gray-100 rounded-lg p-2 text-2xl cursor-pointer text-gray-600 hover:text-gray-800"
            onClick={onClose}
            data-amp-track-section="Email Custom Folders"
            data-amp-track-action="Close_Click"
            data-amp-track-description="Close Custom Folders"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          {destinationsState === 'loading' && (
            <p role="status" className="text-sm text-gray-600">Loading folders…</p>
          )}

          {destinationsState === 'error' && (
            <div role="alert" className="text-sm text-red-600">{LOAD_DESTINATIONS_ERROR_COPY}</div>
          )}

          {destinationsState === 'loaded' && (
            <>
              <ul className="flex flex-col gap-2">
                {systemDestinations.map((destination) => (
                  <li
                    key={destination.destinationKey}
                    className="flex items-center justify-between p-2 rounded-md bg-slate-50 border border-slate-200"
                  >
                    <span className="text-sm text-gray-800">{destination.displayLabel}</span>
                    <span className="text-xs text-gray-500">System folder</span>
                  </li>
                ))}

                {customDestinations.length === 0 && (
                  <li className="text-sm text-gray-600">
                    No custom folders yet. Add a folder to route specific emails.
                  </li>
                )}

                {customDestinations.map((destination) => (
                  <li
                    key={destination.destinationKey}
                    className="flex flex-col gap-2 p-2 rounded-md border border-slate-200"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium text-gray-800 truncate">{destination.displayLabel}</span>
                        {destination.routingInstructions && (
                          <span
                            className="text-xs text-gray-500 truncate"
                            title={destination.routingInstructions}
                          >
                            {destination.routingInstructions}
                          </span>
                     
                        )}
                        {destination.shadowed && (
                          <span className="text-xs text-red-600">{SHADOWED_REASON_COPY}</span>
                        )}
                      </div>
                      {confirmingKey !== destination.destinationKey && (
                        <button
                          type="button"
                          onClick={() => handleRequestRemove(destination.destinationKey)}
                          disabled={removingKey === destination.destinationKey}
                          className="shrink-0 text-xs text-red-600 hover:text-red-700 bg-transparent hover:bg-red-50 border-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Remove from Geoffrey
                        </button>
                      )}
                    </div>

                    {confirmingKey === destination.destinationKey && (
                      <div className="flex flex-col gap-2 bg-red-50 border border-red-100 rounded-md p-2">
                        <p className="text-xs text-gray-700">
                          Remove this folder from Geoffrey? The folder and its emails will stay in your mailbox.
                        </p>
                        {removeErrorByKey[destination.destinationKey] && (
                          <p role="alert" className="text-xs text-red-600">
                            {removeErrorByKey[destination.destinationKey]}
                          </p>
                        )}
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={handleCancelRemove}
                            disabled={removingKey === destination.destinationKey}
                            className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => handleConfirmRemove(destination.destinationKey)}
                            disabled={removingKey === destination.destinationKey}
                            className="text-xs px-2 py-1 rounded-md border border-red-300 bg-red-600 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {removingKey === destination.destinationKey ? 'Removing…' : 'Remove'}
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>

              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  {customDestinations.length} of {maxCustomDestinations} custom folders
                </span>
                <button
                  type="button"
                  onClick={onOpenCustomFolderModal}
                  disabled={atCustomLimit}
                  data-amp-track-section="Email Custom Folders"
                  data-amp-track-action="Add_New_Folder_Click"
                  data-amp-track-description="Add New Folder"
                  className="text-sm text-sky-600 hover:text-sky-700 bg-transparent hover:bg-sky-50 border-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 disabled:text-gray-400"
                >
                  Add New Folder
                </button>
              </div>
              {atCustomLimit && (
                <p className="text-xs text-gray-500">
                  5 of 5 custom folders configured. Remove one to add another.
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-8">
          <button
            type="button"
            className="py-3 px-6 rounded-md text-base cursor-pointer transition-colors duration-200 bg-gray-100 border border-gray-300 text-gray-800 hover:bg-gray-200"
            onClick={onBack}
            data-amp-track-section="Email Custom Folders"
            data-amp-track-action="Back_Click"
            data-amp-track-description="Back to Email Preferences"
          >
            Back
          </button>
        </div>
      </div>
    </div>
  )
}

export default EmailCustomFolders
